import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, 
  TableBody, TableContainer, Paper, Button, IconButton, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, MenuItem, TablePagination, FormControlLabel, Switch
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Edit as EditIcon } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import SkeletonBlock from '../ui/SkeletonBlock';
import { useToast } from '../ToastProvider';

const STATUS_OPTIONS = ['Available', 'Assigned', 'Maintenance', 'Retired', 'Lost'];

export default function EquipmentInventoryAdmin() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form
  const [form, setForm] = useState({
    subcategoryId: '', brand: '', model: '', serialNumber: '',
    purchasePrice: '', purchaseDate: '', supplier: '',
    warrantyExpiration: '', notes: '', status: 'Available'
  });
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [formCategoryId, setFormCategoryId] = useState('');
  
  const toast = useToast();

  useEffect(() => { loadInitialData(); }, []);
  useEffect(() => { loadItems(); }, [filterStatus, showInactive]);

  const loadInitialData = async () => {
    try {
      const [cats, subs] = await Promise.all([
        equipmentService.getCategories(),
        equipmentService.getSubcategories()
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setSubcategories(Array.isArray(subs) ? subs : []);
    } catch (e) { console.error('Failed to load initial data', e); }
    loadItems();
  };

  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await equipmentService.getItems(null, filterStatus || null, showInactive);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  // Filter items by category on frontend
  const filteredItems = useMemo(() => {
    if (!filterCategoryId) return items;
    return items.filter(i => {
      const sub = subcategories.find(s => s.id === i.subcategoryId);
      return sub && sub.categoryId === parseInt(filterCategoryId);
    });
  }, [items, filterCategoryId, subcategories]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Form category change - filter subcategories
  useEffect(() => {
    if (formCategoryId) {
      setFilteredSubcategories(subcategories.filter(s => s.categoryId === parseInt(formCategoryId)));
    } else {
      setFilteredSubcategories([]);
    }
    setForm(f => ({ ...f, subcategoryId: '' }));
  }, [formCategoryId, subcategories]);

  const resetForm = () => {
    setForm({
      subcategoryId: '', brand: '', model: '', serialNumber: '',
      purchasePrice: '', purchaseDate: '', supplier: '',
      warrantyExpiration: '', notes: '', status: 'Available'
    });
    setFormCategoryId('');
    setEditingItem(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = async (item) => {
    try {
      const detail = await equipmentService.getItemById(item.id);
      const sub = subcategories.find(s => s.id === detail.subcategoryId);
      setFormCategoryId(sub?.categoryId?.toString() || '');
      setForm({
        subcategoryId: detail.subcategoryId?.toString() || '',
        brand: detail.brand || '',
        model: detail.model || '',
        serialNumber: detail.serialNumber || '',
        purchasePrice: detail.purchasePrice?.toString() || '',
        purchaseDate: detail.purchaseDate ? detail.purchaseDate.split('T')[0] : '',
        supplier: detail.supplier || '',
        warrantyExpiration: detail.warrantyExpiration ? detail.warrantyExpiration.split('T')[0] : '',
        notes: detail.notes || '',
        status: detail.status || 'Available'
      });
      setEditingItem(detail);
      setDialogOpen(true);
    } catch (e) {
      toast.error('Failed to load item details');
    }
  };

  const handleSave = async () => {
    if (!form.subcategoryId) {
      toast.error('Please select a subcategory');
      return;
    }
    try {
      const payload = {
        subcategoryId: parseInt(form.subcategoryId),
        brand: form.brand || null,
        model: form.model || null,
        serialNumber: form.serialNumber || null,
        purchasePrice: form.purchasePrice ? parseFloat(form.purchasePrice) : null,
        purchaseDate: form.purchaseDate || null,
        supplier: form.supplier || null,
        warrantyExpiration: form.warrantyExpiration || null,
        notes: form.notes || null,
        status: form.status
      };
      if (editingItem) {
        await equipmentService.updateItem(editingItem.id, payload);
        toast.success('Item updated');
      } else {
        await equipmentService.createItem(payload);
        toast.success('Item created');
      }
      setDialogOpen(false);
      resetForm();
      loadItems();
    } catch (e) {
      toast.error(e?.response?.data || 'Failed to save item');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'success';
      case 'Assigned': return 'primary';
      case 'Maintenance': return 'warning';
      case 'Retired': return 'default';
      case 'Lost': return 'error';
      default: return 'default';
    }
  };

  if (loading && items.length === 0) return <SkeletonBlock lines={10} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Equipment Inventory</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<AddIcon />} variant="contained" onClick={openAddDialog}>Add Item</Button>
          <IconButton onClick={loadItems} title="Refresh"><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select label="Category" size="small" sx={{ minWidth: 180 }}
          value={filterCategoryId} onChange={e => { setFilterCategoryId(e.target.value); setPage(0); }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        <TextField
          select label="Status" size="small" sx={{ minWidth: 140 }}
          value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <FormControlLabel
          control={<Switch checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />}
          label="Show Inactive"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredItems.length} item(s)
        </Typography>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Serial #</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedItems.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No items found</TableCell></TableRow>
            ) : paginatedItems.map(item => (
              <TableRow key={item.id} sx={{ opacity: item.isActive ? 1 : 0.5 }}>
                <TableCell>{item.categoryName}</TableCell>
                <TableCell>{item.subcategoryName}</TableCell>
                <TableCell>{item.brand || '-'}</TableCell>
                <TableCell>{item.model || '-'}</TableCell>
                <TableCell>{item.serialNumber || '-'}</TableCell>
                <TableCell>
                  <Chip label={item.status} size="small" color={getStatusColor(item.status)} />
                </TableCell>
                <TableCell>{item.currentAssigneeName || '-'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEditDialog(item)} title="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={filteredItems.length} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 15, 25, 50]}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select label="Category" size="small" required
              value={formCategoryId} onChange={e => setFormCategoryId(e.target.value)}
            >
              <MenuItem value="">Select Category</MenuItem>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField
              select label="Item Type" size="small" required disabled={!formCategoryId}
              value={form.subcategoryId} onChange={e => setForm(f => ({ ...f, subcategoryId: e.target.value }))}
            >
              <MenuItem value="">Select Type</MenuItem>
              {filteredSubcategories.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Brand" size="small" fullWidth
                value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} />
              <TextField label="Model" size="small" fullWidth
                value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} />
            </Box>
            <TextField label="Serial Number" size="small"
              value={form.serialNumber} onChange={e => setForm(f => ({ ...f, serialNumber: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Purchase Price" size="small" type="number" fullWidth
                value={form.purchasePrice} onChange={e => setForm(f => ({ ...f, purchasePrice: e.target.value }))} />
              <TextField label="Purchase Date" size="small" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.purchaseDate} onChange={e => setForm(f => ({ ...f, purchaseDate: e.target.value }))} />
            </Box>
            <TextField label="Supplier" size="small"
              value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} />
            <TextField label="Warranty Expiration" size="small" type="date" InputLabelProps={{ shrink: true }}
              value={form.warrantyExpiration} onChange={e => setForm(f => ({ ...f, warrantyExpiration: e.target.value }))} />
            {editingItem && (
              <TextField
                select label="Status" size="small"
                value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Notes" size="small" multiline rows={2}
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editingItem ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
